import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getChainExplorerTxUrl } from "@rozoai/intent-common";
import { IconButton } from "@stellar/design-system";
import { formatAddress, formatAmount } from "helpers/formatter";
import { useRef, useState } from "react";
import { getAmountIn, getAmountInAsset, getDepositTokenCode, getFromAddress, getWithdrawMemo, } from "types";
import { CheckIcon, CopyIcon, getAssetIcon, Stellar } from "./Icons";
export var getStatusConfig = function (status) {
    var statusMap = {
        incomplete: {
            label: "Loading",
            color: "gray",
            description: "Transaction is in progress",
        },
        pending_anchor: {
            label: "Pending Anchor",
            color: "orange",
            description: "Anchor must verify KYC information",
        },
        pending_user: {
            label: "Pending User",
            color: "blue",
            description: "User action required before sending funds",
        },
        pending_user_transfer_start: {
            label: "Pending User Transfer",
            color: "purple",
            description: "Anchor is ready to receive funds",
        },
        pending_user_transfer_complete: {
            label: "Pending User Transfer Complete",
            color: "teal",
            description: "Funds ready for pickup",
        },
        pending_trust: {
            label: "Pending Trustline",
            color: "yellow",
            description: "Trustline to asset required",
        },
        completed: {
            label: "Completed",
            color: "green",
            description: "Transaction completed successfully",
        },
        error: {
            label: "Error",
            color: "red",
            description: "Transaction encountered an error",
        },
        expired: {
            label: "Expired",
            color: "red",
            description: "Transaction expired",
        },
    };
    return (statusMap[status] || {
        label: status,
        color: "gray",
        description: status,
    });
};
export var TransactionDetailsCard = function (_a) {
    var info = _a.info, timeRemaining = _a.timeRemaining;
    var _b = useState(false), copiedAddress = _b[0], setCopiedAddress = _b[1];
    var handleCopyAddress = function () {
        navigator.clipboard.writeText(window.location.href);
        setCopiedAddress(true);
        setTimeout(function () { return setCopiedAddress(false); }, 2000);
    };
    return (_jsxs("div", { className: "TxnInfo__card", children: [_jsx("div", { className: "TxnInfo__card-header", children: "Transaction Details" }), _jsxs("div", { className: "TxnInfo__card-content", children: [_jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Rozo ID" }), _jsxs("div", { className: "TxnInfo__detail-value", children: [_jsx("span", { className: "TxnInfo__detail-text", children: info.id }), _jsx(IconButton, { altText: "Copy", onClick: handleCopyAddress, icon: copiedAddress ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {}) })] })] }), _jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Status" }), _jsx("div", { className: "TxnInfo__detail-value", children: _jsx("span", { className: "TxnInfo__status-badge TxnInfo__status-badge--".concat(getStatusConfig(timeRemaining === "Expired" && info.status !== "completed"
                                        ? "expired"
                                        : info.status).color), title: getStatusConfig(timeRemaining === "Expired" && info.status !== "completed"
                                        ? "expired"
                                        : info.status).description, children: getStatusConfig(timeRemaining === "Expired" && info.status !== "completed"
                                        ? "expired"
                                        : info.status).label }) })] }), timeRemaining !== "Expired" &&
                        info.status !== "completed" &&
                        timeRemaining !== "" && (_jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Expires in" }), _jsx("div", { className: "TxnInfo__detail-value", children: _jsx("span", { className: "TxnInfo__detail-text", children: timeRemaining }) })] })), [
                        "pending_user",
                        "pending_user_transfer_start",
                        "pending_user_transfer_complete",
                    ].includes(info.status) && (_jsx("div", { className: "TxnInfo__detail-row", role: "alert", style: {
                            backgroundColor: "#edf7ff",
                            border: "1px solid #b6e0fe",
                            borderRadius: 8,
                            padding: "12px 14px",
                        }, children: _jsx("span", { className: "TxnInfo__detail-value", style: { color: "#0b5ea8", maxWidth: "100%" }, children: "Once payment is confirmed, assets will arrive shortly." }) }))] })] }));
};
export var PaymentInfoCard = function (_a) {
    var _b, _c, _d, _e;
    var isDeposit = _a.isDeposit, info = _a.info, showUnExpiredContent = _a.showUnExpiredContent;
    var showToAddress = (isDeposit ? (_b = info.rozo) === null || _b === void 0 ? void 0 : _b.source_receiving_address : getFromAddress(info)) &&
        showUnExpiredContent;
    var depositTokenCode = isDeposit ? getDepositTokenCode(info) : null;
    var isEurcDeposit = isDeposit && depositTokenCode === "EURC";
    var withdrawMemo = !isDeposit ? getWithdrawMemo(info) : null;
    var sourceChainId = Number((_c = info.rozo) === null || _c === void 0 ? void 0 : _c.source_chain_id);
    var sourceTxHash = (_d = info.rozo) === null || _d === void 0 ? void 0 : _d.source_tx_hash;
    var sourceExplorerTxUrl = !isDeposit &&
        sourceTxHash &&
        Number.isFinite(sourceChainId) &&
        sourceChainId > 0
        ? getChainExplorerTxUrl(sourceChainId, sourceTxHash)
        : undefined;
    var _f = useState(false), copiedAddress = _f[0], setCopiedAddress = _f[1];
    var _g = useState(false), showAmountHint = _g[0], setShowAmountHint = _g[1];
    var _h = useState(false), copiedMemo = _h[0], setCopiedMemo = _h[1];
    var amountHintRef = useRef(null);
    var handleCopyAddress = function () {
        var _a;
        navigator.clipboard.writeText(isDeposit
            ? ((_a = info.rozo) === null || _a === void 0 ? void 0 : _a.source_receiving_address) || ""
            : getFromAddress(info));
        setCopiedAddress(true);
        setTimeout(function () { return setCopiedAddress(false); }, 2000);
    };
    var handleCopyMemo = function () {
        navigator.clipboard.writeText(withdrawMemo || "");
        setCopiedMemo(true);
        setTimeout(function () { return setCopiedMemo(false); }, 2000);
    };
    return (_jsxs("div", { className: "TxnInfo__card", children: [_jsx("div", { className: "TxnInfo__card-header", children: "Payment Information" }), _jsxs("div", { className: "TxnInfo__card-content", children: [_jsxs("div", { className: "TxnInfo__detail-row", children: [_jsxs("span", { className: "TxnInfo__detail-label", children: ["Amount", isDeposit && (_jsxs("span", { ref: amountHintRef, className: "TxnInfo__qr-hint-trigger", style: { backgroundColor: "white" }, role: "button", tabIndex: 0, onMouseEnter: function () { return setShowAmountHint(true); }, onMouseLeave: function () { return setShowAmountHint(false); }, onClick: function () { return setShowAmountHint(function (v) { return !v; }); }, onKeyDown: function (e) {
                                            return e.key === "Enter" && setShowAmountHint(function (v) { return !v; });
                                        }, children: ["?", showAmountHint && (_jsx("span", { className: "TxnInfo__qr-token-popup TxnInfo__qr-token-popup--left", children: "We will detect the actual deposit amount." }))] }))] }), _jsxs("span", { className: "TxnInfo__detail-value TxnInfo__detail-value--amount", children: [isDeposit
                                        ? isEurcDeposit
                                            ? getAssetIcon("EURC", {
                                                className: "TxnInfo__detail-icon",
                                            })
                                            : null
                                        : getAssetIcon(getAmountInAsset(info), {
                                            className: "TxnInfo__detail-icon",
                                        }), formatAmount(getAmountIn(info), isDeposit && depositTokenCode !== "EURC"
                                        ? ""
                                        : getAmountInAsset(info))] })] }), withdrawMemo && showUnExpiredContent && (_jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Memo" }), _jsxs("div", { className: "TxnInfo__detail-value", children: [_jsx("span", { className: "TxnInfo__detail-text", children: withdrawMemo }), _jsx(IconButton, { altText: "Copy Memo", onClick: handleCopyMemo, icon: copiedMemo ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {}) })] })] })), _jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: isDeposit ? "To address" : "From address (Stellar)" }), _jsx("div", { className: "TxnInfo__detail-value", children: showToAddress ? (_jsxs(_Fragment, { children: [isDeposit ? null : (_jsx(Stellar, { className: "TxnInfo__detail-icon" })), _jsx("span", { className: "TxnInfo__detail-text", children: isDeposit
                                                ? formatAddress(((_e = info.rozo) === null || _e === void 0 ? void 0 : _e.source_receiving_address) || "")
                                                : formatAddress(getFromAddress(info)) }), _jsx(IconButton, { altText: "Copy Address", onClick: handleCopyAddress, icon: copiedAddress ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {}) })] })) : (_jsx("span", { className: "TxnInfo__detail-text", children: "-" })) })] }), sourceExplorerTxUrl && (_jsxs("div", { className: "TxnInfo__detail-row", children: [_jsx("span", { className: "TxnInfo__detail-label", children: "Explorer" }), _jsx("div", { className: "TxnInfo__detail-value", children: _jsx("a", { className: "TxnInfo__detail-text", href: sourceExplorerTxUrl, target: "_blank", rel: "noopener noreferrer", children: "View transaction" }) })] }))] })] }));
};
